import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Container, Alert, Spinner } from 'react-bootstrap';
import './PublicDocumentView.scss';

/**
 * Data structure matching backend PublicDocumentVO
 * (Kept for potential future use with receipt template)
 */
interface PublicDocumentData {
    documentNumber?: number;
    invoiceNumber?: number;
    creditNoteNumber?: number;
    invoiceDate?: string;
    creditNoteDate?: string;
    documentType?: string; // INVOICE, RECEIPT
    paymentType?: string; // CASH, BANK
    paid?: boolean;
    isStorno?: boolean;
    canceledNumber?: number;
    canceledDate?: string;
    total?: number;
    netAmount?: number;
    vatAmount?: number;
    fee?: number;
    discount?: number;
    countMonths?: string;
    currency?: string;
    clientName?: string;
    clientCity?: string;
    clientAddress?: string;
    eik?: string;
    vat?: string;
    mol?: string;
    sellerName?: string;
    sellerVat?: string;
    sellerAddress?: string;
    sellerCity?: string;
    distributorName?: string;
    items?: {
        name: string;
        quantity: number;
        price: number;
        total: number;
    }[];
}

// Translation map for item type prefixes (kept for potential future use)
const itemTypeTranslations: { [key: string]: string } = {
    'DEVICE': 'Устройство',
    'OIL': 'Масло',
    'BATTERY': 'Батерия',
    'CONSUMABLE': 'Консуматив',
    'OTHER': 'Друго'
};

// Helper function to translate item names (kept for potential future use)
const translateItemName = (name: string): string => {
    for (const [key, translation] of Object.entries(itemTypeTranslations)) {
        if (name.startsWith(key + ' ')) {
            return translation + ' ' + name.substring(key.length + 1);
        }
        if (name === key) {
            return translation;
        }
    }
    return name;
};

// EUR to BGN exchange rate (fixed rate) - kept for potential future use
const EUR_TO_BGN_RATE = 1.95583;

/**
 * Public document visualization page - displays A4 PDF from backend.
 * Accessed via QR code scan on fiscal receipts.
 * URL format: /docs/view/{token}/i (invoice) or /docs/view/{token}/s (storno)
 * Legacy format supported: /docs/view/{token}?type=invoice
 */
export const PublicDocumentView: React.FC = () => {
    const { token, docTypeCode } = useParams<{ token: string; docTypeCode?: string }>();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);

    // Get document type from URL path suffix or query param (for backwards compatibility)
    // /i = invoice, /s = storno/creditnote
    const getDocType = (): string => {
        if (docTypeCode === 'i') return 'invoice';
        if (docTypeCode === 's') return 'creditnote';
        return searchParams.get('type') || 'invoice';
    };
    const docType = getDocType();

    useEffect(() => {
        const fetchPdf = async () => {
            if (!token) {
                setError('Невалиден линк към документ');
                setLoading(false);
                return;
            }

            try {
                // Build backend PDF URL
                const apiUrl = process.env.REACT_APP_API_URL || '';
                const pdfUrl = docTypeCode
                    ? `${apiUrl}client-service/public/docs/view/${token}/${docTypeCode}`
                    : `${apiUrl}client-service/public/docs/view/${token}?type=${docType}`;

                // Fetch PDF as blob
                const response = await fetch(pdfUrl);

                if (!response.ok) {
                    if (response.status === 404) {
                        setError('Документът не е намерен');
                    } else if (response.status === 429) {
                        setError('Твърде много повторни зареждане на документа, моля изчакайте.');
                    } else {
                        setError('Грешка при зареждане на документа');
                    }
                    setLoading(false);
                    return;
                }

                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                setPdfBlobUrl(blobUrl);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching PDF:', err);
                setError('Грешка при зареждане на документа');
                setLoading(false);
            }
        };

        fetchPdf();

        // Cleanup blob URL on unmount
        return () => {
            if (pdfBlobUrl) {
                URL.revokeObjectURL(pdfBlobUrl);
            }
        };
    }, [token, docType, docTypeCode]);

    if (loading) {
        return (
            <Container className="receipt-container loading" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Зареждане на документа...</p>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="receipt-container error" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Alert variant="danger" className="text-center">
                    <Alert.Heading>Грешка</Alert.Heading>
                    <p>{error}</p>
                </Alert>
            </Container>
        );
    }

    // Display PDF from blob URL
    return (
        <div className="pdf-viewer-wrapper" style={{ width: '100%', height: '100vh' }}>
            <iframe
                src={pdfBlobUrl || ''}
                title="Document PDF"
                width="100%"
                height="100%"
                style={{ border: 'none' }}
            />
        </div>
    );

    /*
    return (
        <div className="receipt-wrapper">
            <div className="receipt">
                {/- Original/Storno Title -/}
                <div className="receipt-section">
                    <div className="receipt-line center bold">
                        {document.isStorno ? 'С Т О Р Н О' : 'О Р И Г И Н А Л'}
                    </div>
                </div>

                {/- Storno Info -/}
                {document.isStorno && (
                    <div className="receipt-section storno-info">
                        <div className="receipt-line">ОПЕРАТОРСКА ГРЕШКА</div>
                        <div className="receipt-line">ОСНОВАНИЕ:</div>
                        {isInvoice && (
                            <div className="receipt-line">
                                КРЕДИТНО ИЗВЕСТИЕ: {padNumber(document.creditNoteNumber, 10)}
                            </div>
                        )}
                    </div>
                )}

                {/- Invoice Number -/}
                {isInvoice && (
                    <div className="receipt-section">
                        <div className="receipt-line center">
                            ФАКТУРА: {padNumber(document.invoiceNumber, 10)}
                        </div>
                    </div>
                )}

                {/- Operator -/}
                <div className="receipt-row">
                    <span>#ОПЕРАТОР {document.distributorName || 'Детелин Стоянов'}
                    </span>
                    <span>#</span>
                </div>

                {/- Item Details -/}
                <div className="receipt-section items">
                    {document.items && document.items.length > 0 ? (
                        document.items.map((item, index) => (
                            <div key={index} className="item-block">
                                <div className="receipt-line">{translateItemName(item.name)}</div>
                                <div className="receipt-row">
                                    <span>ЕДИНИЧНА ЦЕНА</span>
                                    <span>{formatPrice(item.price)}</span>
                                </div>
                                <div className="receipt-row">
                                    <span>КОЛИЧЕСТВО</span>
                                    <span>{item.quantity?.toFixed(3).replace('.', ',')}</span>
                                </div>
                                <div className="receipt-row">
                                    <span>СУМА</span>
                                    <span>{formatPrice(item.total)} Б</span>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="item-block">
                            <div className="receipt-line">НАЕМ НА</div>
                            <div className="receipt-line">АРОМАТИЗИРАЩА МАШИНА</div>
                            {isInvoice && document.countMonths && (
                                <>
                                    <div className="receipt-row">
                                        <span>ЕДИНИЧНА ЦЕНА</span>
                                        <span>{formatPrice((document.total || 0) / parseFloat(document.countMonths))}</span>
                                    </div>
                                    <div className="receipt-row">
                                        <span>КОЛИЧЕСТВО</span>
                                        <span>{parseFloat(document.countMonths).toFixed(3).replace('.', ',')}</span>
                                    </div>
                                </>
                            )}
                            <div className="receipt-row">
                                <span>СУМА</span>
                                <span>{formatPrice(document.total)} Б</span>
                            </div>
                        </div>
                    )}
                </div>

                {/- Euro Total Section -/}
                <div className="receipt-section totals">
                    <div className="receipt-row total-row bold">
                        <span>ОБЩА СУМА ЕВРО</span>
                        <span>{formatEuro(document.total)}</span>
                    </div>
                    <div className="receipt-row">
                        <span>ОБЩА СУМА В ЛЕВА</span>
                        <span>{formatPrice(document.total)}</span>
                    </div>
                    <div className="receipt-line small">ОБМ. КУРС 1 ЕВРО=1,95583 ЛВ</div>
                    <div className="receipt-line">Тотал:</div>
                    <div className="receipt-row">
                        <span>В БРОЙ ЕВРО</span>
                        <span>{formatEuro(document.total)}</span>
                    </div>
                </div>

                {/- VAT Breakdown -/}
                <div className="receipt-section vat-info">
                    <div className="receipt-row">
                        <span>ОБОРОТ Б</span>
                        <span>{formatEuro(document.total)}</span>
                    </div>
                    <div className="receipt-row">
                        <span>ДДС ГРУПА Б=20,00%</span>
                        <span>{formatEuro(document.vatAmount)}</span>
                    </div>
                    <div className="receipt-row">
                        <span>НЕТО СТОЙНОСТ Б</span>
                        <span>{formatEuro(document.netAmount)}</span>
                    </div>
                </div>

                {/- Dashed Separator -/}
                <div className="receipt-separator dashed"></div>

                {/- Seller/Receiver Signatures -/}
                <div className="receipt-section signatures">
                    <div className="receipt-line">ПРОДАВАЧ:</div>
                    <div className="receipt-line center">..........................</div>
                    <div className="receipt-line">ПОЛУЧАТЕЛ:</div>
                    <div className="receipt-line center">..........................</div>
                </div>
                
                {/- Client Info (for invoices) -/}
                {isInvoice && document.clientName && (
                    <div className="receipt-section client-info">
                        <div className="receipt-line">КУПУВАЧ: {document.clientName}</div>
                        <div className="receipt-line">ИДЕНТ. No.: {document.eik}</div>
                        {document.vat && (
                            <div className="receipt-line">ЗДДС No.: BG{document.vat}</div>
                        )}
                        <div className="receipt-line">АДРЕС: {document.clientCity}, {document.clientAddress}</div>
                    </div>
                )}

                {/- Fiscal Receipt Label -/}
                <div className="receipt-section fiscal-label">
                    <div className="receipt-line center">
                        <span className="fiscal-icon">🇧🇬</span> ФИСКАЛЕН БОН
                    </div>
                </div>
            </div>
        </div>
    );
    ==================================================================================== */
};
